/**
 * src/lib/capture/buildCaptureDeps.js — v24.0 Knowledge Vault, Phase 102 slice E.
 *
 * Wires the REAL side effects for captureAndFile() from the existing api client
 * + Supabase storage + Web Crypto. captureAndFile owns the routing/invariants
 * (pure, unit-tested with injected deps); THIS module is the only place those
 * deps touch the network/disk, so the routing logic stays transport-free.
 *
 * `confirmScope` is the ONE dep the UI must supply (the always-shown one-screen
 * ScopeConfirmSheet, resolved via a promise bridge in UploadFilesPanel) — every
 * other dep is wired here. All wrappers THROW on a falsey/`success:false`
 * response so captureAndFile's error path (and the panel's per-item catch) sees
 * a real Error rather than a silent partial.
 */

import { apiFetch, API_BASE } from '../api/client';
import { supabase } from '../supabase';
import { uploadInvoiceFiles } from '../api/cfo';
import { makeThumbnailBlob } from '../api/photos';
import { isCadFileName, CAD_MIME } from '../../../lib/files/cadFiles.mjs';

// Buckets (mirror the photos subsystem): images land in photo-buffer, every
// other (<=25MB) document in project-documents. Large files (>25MB) never reach
// here -- captureAndFile routes them to presign() instead.
const IMAGE_BUCKET = 'photo-buffer';
const DOC_BUCKET = 'project-documents';
// Public, hot, kept indefinitely — same tier the camera path writes to.
const THUMB_BUCKET = 'photo-thumbs';

/** SHA-256 hex of a File's bytes -- the dedup identity (mirrors photos.sha256Hex). */
async function sha256(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Read a short text preview for the classifier -- ONLY for text-ish files. Never
 * decode binary as text (it would yield garbage and waste a read). Returns '' on
 * any unreadable/binary input.
 */
async function readPreview(file) {
  const mime = file?.type || '';
  const isTextish = mime.startsWith('text/') || mime === 'application/json';
  if (!isTextish) return '';
  try {
    const text = await file.text();
    return text.slice(0, 4096);
  } catch {
    // spine-emit-ok: preview is a best-effort classifier hint; an unreadable
    // text file simply classifies without a preview (no behavioural impact).
    return '';
  }
}

/** Bucket a document lands in, by mime. */
function bucketFor(file) {
  // TICKET-908: .dxf/.dwg often report image/vnd.* — a drawing, never a photo.
  if (isCadFileName(file?.name)) return DOC_BUCKET;
  return (file?.type || '').startsWith('image/') ? IMAGE_BUCKET : DOC_BUCKET;
}

/**
 * Downscale + upload the hot public thumbnail, mirroring the camera path
 * (photoApplier). Returns the absolute public URL, or null if a thumb could not
 * be made or stored — never throws, because a capture must not fail over a
 * thumbnail. `thumbUrlFromDoc` requires an absolute URL, so the public URL is
 * what we persist, not the object path.
 */
async function makeThumb(objectName, file) {
  try {
    const blob = await makeThumbnailBlob(file);
    if (!blob) return null;
    const thumbObject = objectName.replace(/\.[a-z0-9]+$/i, '.jpg');
    // TICKET-658: ArrayBuffer, not Blob — storage-js wraps a Blob body in
    // FormData with an empty field name, which WebKit intermittently
    // mis-serializes on larger payloads ("No content provided" server-side).
    const { error } = await supabase.storage
      .from(THUMB_BUCKET)
      .upload(thumbObject, await blob.arrayBuffer(), { contentType: 'image/jpeg', upsert: true, cacheControl: '3600' });
    if (error && !/duplicate|already exists|conflict/i.test(error.message || '')) return null;
    const { data } = supabase.storage.from(THUMB_BUCKET).getPublicUrl(thumbObject);
    return data?.publicUrl || null;
  } catch {
    // spine-emit-ok: src/lib/capture is not a spine hot zone, and a thumbnail is
    // an optimisation — the tile falls back to the lazily-presigned original.
    return null;
  }
}

/** Throw a real Error from an apiFetch result that signals failure. */
function unwrap(res, fallbackMsg) {
  if (!res || res.success === false || res.error) {
    throw new Error((res && res.error) || fallbackMsg);
  }
  return res;
}

/**
 * @param {object} opts
 * @param {(prefill:object)=>Promise<object|null>} opts.confirmScope -- the
 *   always-shown scope-confirm bridge (supplied by UploadFilesPanel).
 * @returns {object} the full deps bag captureAndFile expects.
 */
export function buildCaptureDeps({ confirmScope }) {
  return {
    sha256,
    readPreview,
    confirmScope,

    // LLM classify -- POST documents.classify with the file metadata + preview.
    async classify(body) {
      const res = await apiFetch(`${API_BASE}/data`, {
        method: 'POST',
        body: JSON.stringify({ action: 'documents.classify', ...body }),
      });
      return unwrap(res, 'classify_failed');
    },

    // Supabase upload (<=25MB) -> returns { file_path, thumb_path? } for the
    // register step.
    async uploadSupabase(file, { content_sha256 } = {}) {
      const bucket = bucketFor(file);
      const lastDot = file.name?.lastIndexOf('.') ?? -1;
      const rawExt = lastDot >= 0 ? file.name.slice(lastDot + 1) : '';
      const ext = rawExt.replace(/[^a-z0-9]/gi, '').slice(0, 8).toLowerCase() || 'bin';
      // Content-addressed object name -> identical bytes collapse to one object.
      const objectName = `${content_sha256 || crypto.randomUUID()}.${ext}`;
      // TICKET-658: ArrayBuffer, not File/Blob — see makeThumb() above.
      const { error } = await supabase.storage
        .from(bucket)
        .upload(objectName, await file.arrayBuffer(), {
          contentType: isCadFileName(file.name) ? CAD_MIME : (file.type || 'application/octet-stream'),
          upsert: true,
        });
      if (error) throw new Error(`upload_failed: ${error.message}`);
      if (bucket !== IMAGE_BUCKET) return { file_path: `${bucket}/${objectName}` };

      // TICKET-472 — a dropped image needs the same 320px public thumbnail the
      // CAMERA path already writes (photoApplier). Without it the gallery tile
      // presigns and downloads the full-resolution original on every load: 54 of
      // 269 prod photos were stuck in exactly that state. Best-effort throughout
      // — a missing thumb costs speed, a failed capture costs the photo.
      const thumb_path = await makeThumb(objectName, file);
      return { file_path: `${bucket}/${objectName}`, ...(thumb_path ? { thumb_path } : {}) };
    },

    // Large-file presign -- POST documents.presign -> { upload_url, object_key, ... }.
    async presign(body) {
      const res = await apiFetch(`${API_BASE}/data`, {
        method: 'POST',
        body: JSON.stringify({ action: 'documents.presign', ...body }),
      });
      return unwrap(res, 'presign_failed');
    },

    // PUT the bytes to the presigned URL (raw fetch -- no auth header, no JSON).
    async putToUrl(url, file) {
      const resp = await fetch(url, { method: 'PUT', body: file });
      if (!resp.ok) throw new Error(`put_failed: ${resp.status}`);
    },

    // File the registry row -- POST documents.register -> { document_id, deduped }.
    async registerDoc(payload) {
      const res = await apiFetch(`${API_BASE}/data`, {
        method: 'POST',
        body: JSON.stringify({ action: 'documents.register', ...payload }),
      });
      return unwrap(res, 'register_failed');
    },

    // File a buffered PHOTO row -- POST photos.register -> { document_id, deduped }.
    // Mirrors registerDoc but targets the dedicated photo pipeline so an image
    // lands in the project's photo gallery (thumbnail/buffer/EXIF/eviction) rather
    // than the generic documents register. buffer_path is the photo-buffer object
    // uploadSupabase produced (`photo-buffer/<sha>.<ext>`); handlePhotosRegister
    // stores it verbatim and dedups on content_sha256.
    async photosRegister(payload) {
      const res = await apiFetch(`${API_BASE}/data`, {
        method: 'POST',
        body: JSON.stringify({ action: 'photos.register', ...payload }),
      });
      return unwrap(res, 'photos_register_failed');
    },

    // File a note into the per-vessel brain -- POST documents.add_note.
    async addNote(payload) {
      const res = await apiFetch(`${API_BASE}/data`, {
        method: 'POST',
        body: JSON.stringify({ action: 'documents.add_note', ...payload }),
      });
      return unwrap(res, 'add_note_failed');
    },

    // Invoice -> the EXISTING invoice-intake endpoint (source:'upload'). Reuses
    // uploadInvoiceFiles (base64 + JWT) so invoices NEVER hit project_documents
    // (AC2). The endpoint accepts a project_id scope only; a deal/vessel-scoped
    // invoice still files (project-less) and is reconcilable in the Invoices area.
    // Returns a batch summary {successful, results[...]} -> normalize to {id, deduped}.
    async invoiceIntake(file, scope = {}) {
      const res = await uploadInvoiceFiles([file], scope.project_id || null);
      if (!res || res.success === false || res.error) {
        throw new Error((res && res.error) || 'invoice_intake_failed');
      }
      const first = Array.isArray(res.results) ? res.results[0] : null;
      if (first && first.status === 'error') {
        throw new Error(first.reason || 'invoice_intake_failed');
      }
      const deduped = !!first && (first.status === 'duplicate' || first.status === 'skipped');
      return { id: first?.invoice_id || first?.existing_invoice_id || null, deduped };
    },
  };
}
